function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function clean(value) {
  return String(value ?? "").trim();
}

function pick(...values) {
  for (const value of values) {
    const candidate = clean(value);
    if (candidate) return candidate;
  }
  return "";
}

function fillMissing(current, defaults) {
  const source = objectValue(current);
  const output = { ...source };
  for (const [key, fallback] of Object.entries(objectValue(defaults))) {
    if (fallback && typeof fallback === "object" && !Array.isArray(fallback)) {
      output[key] = fillMissing(source[key], fallback);
      continue;
    }
    if (source[key] === undefined || source[key] === null || source[key] === "") output[key] = fallback;
  }
  return output;
}

export function createMegaMenuVisualDefaults(theme = {}, designTokens = {}, navigation = {}) {
  const themeSource = objectValue(theme);
  const tokenSource = objectValue(designTokens);
  const colors = objectValue(tokenSource.colors || themeSource.designTokens?.colors);
  const primary = pick(colors.primary, themeSource.primaryColor, "#E44E31");
  const secondary = pick(colors.secondary, themeSource.secondaryColor, "#494A98");
  const surface = pick(colors.surface, themeSource.surfaceColor, "#F7F8FC");
  const background = pick(colors.background, themeSource.backgroundColor, "#EEF0F5");
  const text = pick(colors.text, themeSource.textColor, "#1A1A2E");
  const muted = pick(colors.mutedText, themeSource.mutedTextColor, "#5B5D73");
  const divider = pick(colors.divider, themeSource.dividerColor, "rgba(26,26,46,.09)");
  const configuredStyle = clean(navigation?.megaMenuStyle).toLowerCase();
  const elegant = configuredStyle === "elegant";

  return {
    surface: {
      backgroundColor: surface,
      borderColor: divider,
      boxShadow: elegant ? "0 28px 72px rgba(18,25,38,.14)" : "0 24px 58px rgba(18,25,38,.14)",
      borderRadius: elegant ? 30 : 22,
    },
    introSurface: { backgroundColor: secondary, borderColor: secondary },
    eyebrow: { color: primary, introColor: "rgba(255,255,255,.70)" },
    title: { color: secondary, introColor: "#FFFFFF" },
    description: { color: muted, introColor: "rgba(255,255,255,.72)" },
    categoriesTitle: { color: configuredStyle === "catalog" || configuredStyle === "auto" ? secondary : text },
    categoriesSubtitle: { color: muted },
    sortLabel: { color: muted },
    latestLabel: { color: muted },
    allItemsButton: {
      textColor: secondary,
      backgroundColor: "transparent",
      borderColor: "transparent",
      hoverTextColor: secondary,
      hoverBackgroundColor: background,
      hoverBorderColor: "transparent",
    },
    introAllItemsButton: {
      textColor: "#FFFFFF",
      backgroundColor: "rgba(255,255,255,.10)",
      borderColor: "transparent",
      hoverTextColor: "#FFFFFF",
      hoverBackgroundColor: "rgba(255,255,255,.16)",
      hoverBorderColor: "transparent",
    },
    statsChip: { textColor: text, backgroundColor: "rgba(26,26,46,.08)", borderColor: "transparent" },
    introStatsChip: { textColor: "#FFFFFF", backgroundColor: "rgba(255,255,255,.10)", borderColor: "transparent" },
    showcaseStatsText: { color: muted },
    categoryRow: {
      labelColor: text,
      countColor: muted,
      iconColor: "rgba(91,93,115,.55)",
      backgroundColor: "transparent",
      borderColor: "transparent",
      hoverBackgroundColor: background,
      hoverBorderColor: divider,
    },
    subcategory: {
      textColor: muted,
      backgroundColor: "transparent",
      hoverTextColor: muted,
      hoverBackgroundColor: "rgba(228,78,49,.05)",
    },
    categoryPill: {
      textColor: muted,
      backgroundColor: background,
      borderColor: "transparent",
      hoverTextColor: muted,
      hoverBackgroundColor: background,
      hoverBorderColor: divider,
    },
    latestItem: {
      textColor: muted,
      backgroundColor: background,
      borderColor: "transparent",
      hoverTextColor: muted,
      hoverBackgroundColor: background,
      hoverBorderColor: divider,
    },
    previewCard: {
      backgroundColor: surface,
      borderColor: divider,
      hoverBorderColor: primary,
      boxShadow: elegant ? "0 14px 34px rgba(18,25,38,.07)" : "none",
      hoverBoxShadow: "0 18px 38px rgba(18,25,38,.10)",
      categoryTextColor: muted,
      titleTextColor: text,
      descriptionTextColor: muted,
      imageBackgroundColor: background,
    },
    loading: { spinnerColor: primary, textColor: muted },
    empty: { backgroundColor: background, borderColor: divider, textColor: muted },
    error: { backgroundColor: "#FFF4E5", borderColor: "#FFB74D", textColor: "#8A4B08", iconColor: "#F57C00" },
    divider: { color: divider },
  };
}

export function materializeMegaMenuVisual(navigation = {}, theme = {}, designTokens = {}) {
  const source = objectValue(navigation);
  return {
    ...source,
    megaMenuVisual: fillMissing(
      source.megaMenuVisual,
      createMegaMenuVisualDefaults(theme, designTokens, source),
    ),
  };
}
